import React, { useState, useEffect } from 'react';
import { useAuthState } from 'react-firebase-hooks/auth';
import { useNavigate } from 'react-router-dom';
import { auth, db } from '../firebase';
import { doc, getDoc, setDoc, serverTimestamp, collection, getDocs, query, where } from 'firebase/firestore';
import { signOut } from 'firebase/auth';
import { encrypt, decryptIfNeeded, decryptIdea, decryptSubtask } from '../utils/encryption';
import { Loader2, CheckCircle2, AlertTriangle, Download, Mail } from 'lucide-react';
import {
  sendFirebaseVerificationEmail,
  reloadCurrentAuthUser,
} from '../utils/emailVerification';

function UserProfile() {
  const [user] = useAuthState(auth);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [emailVerifyResendBusy, setEmailVerifyResendBusy] = useState(false);
  const [emailVerifyReloadBusy, setEmailVerifyReloadBusy] = useState(false);
  const [emailVerifyNotice, setEmailVerifyNotice] = useState(null);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: ''
  });

  const encryptionKey = process.env.REACT_APP_ENCRYPTION_KEY;

  const encryptSensitiveData = (data) => {
    return {
      ...data,
      firstName: encrypt(data.firstName, encryptionKey),
      lastName: encrypt(data.lastName, encryptionKey),
      email: encrypt(data.email, encryptionKey)
    };
  };

  const decryptSensitiveData = (data) => {
    return {
      ...data,
      // Use decryptIfNeeded for backward compatibility with existing plain text data
      firstName: decryptIfNeeded(data.firstName, encryptionKey),
      lastName: decryptIfNeeded(data.lastName, encryptionKey),
      email: decryptIfNeeded(data.email, encryptionKey)
    };
  };

  useEffect(() => {
    const fetchUserProfile = async () => {
      if (!user) return;
      
      try {
        setLoading(true);
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        
        if (userDoc.exists()) {
          const data = userDoc.data();
          const decryptedData = decryptSensitiveData(data);
          setFormData({
            ...decryptedData,
            email: user.email // Always use auth email
          });
        } else {
          // Initialize new user profile
          setFormData({
            firstName: '',
            lastName: '',
            email: user.email // Set email from auth
          });
        }
      } catch (error) {
        console.error('Error fetching profile:', error);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchUserProfile();
  }, [user]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!user) return;

    try {
      setLoading(true);
      setError(null);
      
      const userRef = doc(db, 'users', user.uid);
      const encryptedData = encryptSensitiveData(formData);

      await setDoc(userRef, {
        ...encryptedData,
        updatedAt: serverTimestamp()
      }, { merge: true });
      
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (error) {
      console.error('Error updating profile:', error);
      setError('Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value
    }));
  };

  const handleResendVerificationEmail = async () => {
    if (!user?.email) return;
    setEmailVerifyNotice(null);
    setEmailVerifyResendBusy(true);
    try {
      const r = await sendFirebaseVerificationEmail();
      if (r === 'sent') {
        setEmailVerifyNotice('Verification email sent. Check your inbox and spam folder.');
      } else if (r === 'already_verified') {
        setEmailVerifyNotice('Already verified. If this page still shows unverified, tap Refresh below.');
      } else if (r === 'no_email') {
        setEmailVerifyNotice('No email address on this sign-in.');
      }
    } catch (e) {
      setEmailVerifyNotice(e?.message || 'Could not send verification email.');
    } finally {
      setEmailVerifyResendBusy(false);
    }
  };

  const handleRefreshEmailVerification = async () => {
    setEmailVerifyNotice(null);
    setEmailVerifyReloadBusy(true);
    try {
      await reloadCurrentAuthUser();
      setEmailVerifyNotice('Session refreshed. If you just clicked the link in your email, status should update above.');
    } catch (e) {
      setEmailVerifyNotice(e?.message || 'Could not refresh.');
    } finally {
      setEmailVerifyReloadBusy(false);
    }
  };

  const handleExportData = async () => {
    if (!user) return;

    try {
      setExporting(true);
      setError(null);

      const encryptionKey = process.env.REACT_APP_ENCRYPTION_KEY;

      // 1. Fetch all ideas
      const ideasRef = collection(db, 'users', user.uid, 'ideas');
      const ideasSnapshot = await getDocs(ideasRef);
      
      const ideasData = ideasSnapshot.docs.map(doc => {
        const data = doc.data();
        const decryptedIdea = decryptIdea(data, encryptionKey);
        
        // Decrypt subtasks
        const decryptedSubtasks = data.subtasks ? data.subtasks.map(subtask => 
          decryptSubtask(subtask, encryptionKey)
        ) : [];
        
        return {
          id: doc.id,
          ...decryptedIdea,
          subtasks: decryptedSubtasks,
          createdAt: data.createdAt?.toDate?.()?.toISOString() || data.createdAt,
          updatedAt: data.updatedAt?.toDate?.()?.toISOString() || data.updatedAt
        };
      });

      // 2. Fetch all tags
      const tagsRef = collection(db, 'tags');
      const tagsQuery = query(tagsRef, where('userId', '==', user.uid));
      const tagsSnapshot = await getDocs(tagsQuery);
      
      const tagsData = tagsSnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          name: decryptIfNeeded(data.name, encryptionKey),
          createdAt: data.createdAt?.toDate?.()?.toISOString() || data.createdAt
        };
      });

      // 3. Get user profile data
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      const userData = userDoc.exists() ? {
        ...decryptSensitiveData(userDoc.data()),
        createdAt: userDoc.data().createdAt?.toDate?.()?.toISOString() || userDoc.data().createdAt,
        updatedAt: userDoc.data().updatedAt?.toDate?.()?.toISOString() || userDoc.data().updatedAt
      } : null;

      // 4. Create export object
      const exportData = {
        exportDate: new Date().toISOString(),
        version: '1.0',
        user: {
          uid: user.uid,
          email: user.email,
          profile: userData
        },
        ideas: ideasData,
        tags: tagsData,
        summary: {
          totalIdeas: ideasData.length,
          totalTags: tagsData.length,
          ideasByStatus: ideasData.reduce((acc, idea) => {
            acc[idea.status] = (acc[idea.status] || 0) + 1;
            return acc;
          }, {})
        }
      };

      // 5. Create and download JSON file
      const dataStr = JSON.stringify(exportData, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `epiphoric-export-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      setSuccess('Data exported successfully!');
      setTimeout(() => setSuccess(false), 5000);
    } catch (error) {
      console.error('Error exporting data:', error);
      setError(`Failed to export data: ${error.message}`);
    } finally {
      setExporting(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (!user) return;

    // Confirm deletion
    const confirmMessage = 'Are you sure you want to delete your account? This action cannot be undone and will permanently delete all your ideas, tags, and profile data.';
    const confirmDelete = window.confirm(confirmMessage);
    
    if (!confirmDelete) return;

    // Double confirmation
    const doubleConfirm = window.confirm('This is your last chance. Are you absolutely sure you want to delete your account and all associated data?');
    
    if (!doubleConfirm) return;

    try {
      setDeleting(true);
      setError(null);

      // Get fresh ID token for authentication
      const idToken = await user.getIdToken(true); // Force refresh
      
      // Determine API URL (same pattern as AuthBridge)
      const hostname = window.location.hostname;
      const port = window.location.port;
      const isLocalNetwork = /^(192\.168\.|10\.|172\.(1[6-9]|2[0-9]|3[0-1])\.)/.test(hostname);
      const isDevelopment = hostname === 'localhost' || 
                            hostname === '127.0.0.1' || 
                            isLocalNetwork ||
                            port === '3000' ||
                            process.env.NODE_ENV === 'development';
      
      const API_BASE_URL = process.env.REACT_APP_API_URL || '';
      const endpointPath = '/api/delete-account';
      const apiUrl = API_BASE_URL
        ? `${API_BASE_URL}${endpointPath}`
        : isDevelopment
          ? `http://127.0.0.1:4000${endpointPath}`
          : `https://us-central1-epiphoric-prod.cloudfunctions.net/api${endpointPath}`;

      console.log('[UserProfile] Calling delete account endpoint:', apiUrl);

      // Call Cloud Function to delete account (handles both email/password and OAuth)
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();

      if (!response.ok) {
        const errorMessage = data.error || 'Failed to delete account';
        const errorCode = data.code || 'UNKNOWN_ERROR';
        console.error('[UserProfile] Delete account failed:', {
          status: response.status,
          error: errorMessage,
          code: errorCode
        });
        throw new Error(errorMessage);
      }

      // Account deleted successfully - sign out and redirect
      console.log('[UserProfile] Account deleted successfully:', {
        success: data.success,
        provider: data.provider
      });
      
      // Sign out the user (this will work even though account is deleted)
      try {
        await signOut(auth);
      } catch (signOutError) {
        // Even if signOut fails, we've already deleted everything
        console.warn('[UserProfile] Sign out failed (account already deleted):', signOutError);
      }
      
      // Redirect to auth page
      navigate('/auth');
      
    } catch (error) {
      console.error('Error deleting account:', error);
      setError(`Failed to delete account: ${error.message}. Please try again or contact support.`);
      setDeleting(false);
    }
  };

  if (loading && !formData.firstName) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 mt-8">
      <form onSubmit={handleSubmit} className="space-y-4">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">My Account</h2>
        
        {error && (
          <div className="p-3 bg-red-100 dark:bg-red-900/30 border border-red-400 dark:border-red-700 text-red-700 dark:text-red-300 rounded text-sm">
            {error}
          </div>
        )}

        {success && (
          <div className="p-3 bg-green-100 dark:bg-green-900/30 border border-green-400 dark:border-green-700 text-green-700 dark:text-green-300 rounded text-sm flex items-center gap-2">
            <CheckCircle2 className="w-5 h-5" />
            Profile updated successfully!
          </div>
        )}

        <div>
          <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            First Name
          </label>
          <input
            id="firstName"
            name="firstName"
            type="text"
            value={formData.firstName}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
          />
        </div>

        <div>
          <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Last Name
          </label>
          <input
            id="lastName"
            name="lastName"
            type="text"
            value={formData.lastName}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
          />
        </div>

        <div>
          <div className="flex flex-wrap items-center gap-2 mb-1">
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Email
            </label>
            {user?.email && (
              <span
                className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${
                  user.emailVerified
                    ? 'bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-200'
                    : 'bg-amber-100 dark:bg-amber-900/40 text-amber-900 dark:text-amber-200'
                }`}
              >
                {user.emailVerified ? (
                  <>
                    <CheckCircle2 className="w-3.5 h-3.5" aria-hidden />
                    Verified
                  </>
                ) : (
                  <>
                    <AlertTriangle className="w-3.5 h-3.5" aria-hidden />
                    Not verified
                  </>
                )}
              </span>
            )}
          </div>
          <input
            id="email"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            disabled
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-gray-100 dark:bg-gray-900 text-gray-500 dark:text-gray-400 cursor-not-allowed"
            aria-describedby="email-verification-help"
          />
          {user?.email && !user.emailVerified && (
            <div
              id="email-verification-help"
              className="mt-3 p-3 rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50/80 dark:bg-amber-900/20 text-sm text-amber-950 dark:text-amber-100"
            >
              <p className="mb-2">
                Verify this address to use the{' '}
                <span className="font-medium">complimentary AI pass</span> (Autopilot and Guided). Other features work without verification.
              </p>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={handleResendVerificationEmail}
                  disabled={emailVerifyResendBusy || emailVerifyReloadBusy}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-amber-600 hover:bg-amber-700 text-white disabled:opacity-50"
                >
                  {emailVerifyResendBusy ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <Mail className="w-3.5 h-3.5" />
                  )}
                  Resend verification email
                </button>
                <button
                  type="button"
                  onClick={handleRefreshEmailVerification}
                  disabled={emailVerifyResendBusy || emailVerifyReloadBusy}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-amber-700 dark:border-amber-500 text-amber-950 dark:text-amber-100 hover:bg-amber-100/80 dark:hover:bg-amber-900/40 disabled:opacity-50"
                >
                  {emailVerifyReloadBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
                  I&apos;ve verified — refresh status
                </button>
              </div>
              {emailVerifyNotice && (
                <p className="mt-2 text-xs text-amber-900/90 dark:text-amber-200/90">{emailVerifyNotice}</p>
              )}
            </div>
          )}
          {user?.email && user.emailVerified && (
            <p id="email-verification-help" className="mt-2 text-xs text-gray-500 dark:text-gray-400">
              This address is confirmed with your sign-in provider. Complimentary AI features that require a verified email are available.
            </p>
          )}
        </div>

        <button
          type="submit"
          disabled={loading}
          className="bg-primary hover:bg-primary/90 text-white font-medium py-2 px-6 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
        >
          {loading ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              Updating...
            </>
          ) : (
            'Update Profile'
          )}
        </button>
      </form>

      {/* Settings Section */}
      <div className="mt-8 pt-8 border-t border-gray-200 dark:border-gray-700">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Settings</h2>
        
        {/* Export Data Section */}
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-4">
          <div className="flex items-start gap-3">
            <Download className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Export Your Data</h3>
              <p className="text-sm text-gray-700 dark:text-gray-300 mb-4">
                Download all your ideas, tags, and profile data as a JSON file. This includes all decrypted data 
                so you can keep a backup or import it elsewhere.
              </p>
              <button
                type="button"
                onClick={handleExportData}
                disabled={exporting}
                className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-6 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {exporting ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Exporting...
                  </>
                ) : (
                  <>
                    <Download className="w-5 h-5" />
                    Export Data
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
        
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Delete Account</h3>
              <p className="text-sm text-gray-700 dark:text-gray-300 mb-4">
                Permanently delete your account and all associated data including ideas, tags, and profile information. This action cannot be undone.
              </p>
              <button
                type="button"
                onClick={handleDeleteAccount}
                disabled={deleting}
                className="bg-red-600 hover:bg-red-700 text-white font-medium py-2 px-6 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {deleting ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Deleting Account...
                  </>
                ) : (
                  'Delete Account'
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default UserProfile;